import type { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  IncentiveCategory,
  IncentiveLifecycleStatus,
  IncentiveStatus,
} from '@varo/graphql-schema-types';
import { useLocalSearchParams } from 'expo-router';
import IncentiveErrorScreen from 'features/incentive/components/IncentiveErrorScreen/IncentiveErrorScreen';
import IncentivePrerequisiteSheet from 'features/incentive/components/IncentivePrerequisiteSheet/IncentivePrerequisiteSheet';
import { IncentiveErrorSteps } from 'features/incentive/helpers/incentiveAnalytics';
import {
  IncentiveDestination,
  resolveIncentiveScreen,
} from 'features/incentive/helpers/resolveIncentiveScreen';
import useIncentiveStatus from 'features/incentive/hooks/useIncentiveStatus';
import useRefreshOnRefocus from 'features/incentive/hooks/useRefreshOnRefocus';
import {
  IncentivePrerequisite,
  IncentivePrerequisiteProduct,
} from 'features/incentive/prerequisite/incentivePrerequisite';
import IncentiveIntroScreen from 'features/incentive/screens/IncentiveIntroScreen/IncentiveIntroScreen';
import IncentiveNotAvailableScreen from 'features/incentive/screens/IncentiveNotAvailableScreen/IncentiveNotAvailableScreen';
import IncentiveTrackerScreen from 'features/incentive/screens/IncentiveTrackerScreen/IncentiveTrackerScreen';
import IncentiveWinnerScreen from 'features/incentive/screens/IncentiveWinnerScreen/IncentiveWinnerScreen';
import { useRef } from 'react';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';

interface IncentiveResolverScreenProps {
  incentiveType: IncentiveCategory;
  prerequisite: IncentivePrerequisite;
}

interface IncentiveDestinationScreenProps {
  incentiveType: IncentiveCategory;
  incentiveProgramId: string;
  status: IncentiveStatus;
  hasMetPrerequisite: boolean;
  prerequisiteProduct: IncentivePrerequisiteProduct | undefined;
  onPrerequisiteRequired: () => void;
  onOptedIn: () => void;
}

function IncentiveDestinationScreen({
  incentiveType,
  incentiveProgramId,
  status,
  hasMetPrerequisite,
  prerequisiteProduct,
  onPrerequisiteRequired,
  onOptedIn,
}: IncentiveDestinationScreenProps) {
  switch (resolveIncentiveScreen(status.status, hasMetPrerequisite)) {
    case IncentiveDestination.Intro:
      return (
        <IncentiveIntroScreen
          incentiveType={incentiveType}
          incentiveProgramId={incentiveProgramId}
          lifecycleStatus={status.status}
          hasMetPrerequisite={hasMetPrerequisite}
          onPrerequisiteRequired={onPrerequisiteRequired}
          onOptedIn={onOptedIn}
        />
      );
    case IncentiveDestination.Tracker:
      return (
        <IncentiveTrackerScreen
          incentiveType={incentiveType}
          incentiveProgramId={incentiveProgramId}
          lifecycleStatus={status.status}
        />
      );
    case IncentiveDestination.Winner:
      return (
        <IncentiveWinnerScreen
          incentiveType={incentiveType}
          incentiveProgramId={incentiveProgramId}
          lifecycleStatus={status.status}
          awards={status.awards}
          prerequisiteProduct={prerequisiteProduct}
        />
      );
    case IncentiveDestination.NotAvailable:
      return <IncentiveNotAvailableScreen incentiveType={incentiveType} />;
  }
}

function IncentiveResolverScreen({
  incentiveType,
  prerequisite,
}: IncentiveResolverScreenProps) {
  const { programId } = useLocalSearchParams<{ programId: string }>();
  const {
    status,
    fetching: isStatusFetching,
    error: statusRequestError,
    statusError,
    refresh: refreshStatus,
  } = useIncentiveStatus(programId);
  // Owned here so an opt-in status change that swaps Intro for Tracker doesn't unmount the sheet.
  const prerequisiteSheetRef = useRef<BottomSheetModal>(null);
  const dismissToTab = useDismissToRootTab();
  const isAwaitingPrerequisite =
    status?.status === IncentiveLifecycleStatus.OptInPendingEligibility &&
    prerequisite.isMet === undefined;

  const handleRefresh = () => {
    refreshStatus();
    prerequisite.refresh();
  };

  useRefreshOnRefocus(handleRefresh);

  const handlePrerequisiteRequired = () => {
    prerequisiteSheetRef.current?.present();
  };

  const handleStartPrerequisitePress = () => {
    prerequisiteSheetRef.current?.dismiss();
    prerequisite.start({
      pathname: '/incentive/[programId]',
      params: { programId },
    });
  };

  const handleSkipPrerequisitePress = () => {
    prerequisiteSheetRef.current?.dismiss();
    dismissToTab(Tabs.HOME);
  };

  if (!status || isAwaitingPrerequisite) {
    const requestError =
      statusRequestError ??
      (isAwaitingPrerequisite ? prerequisite.error : undefined);

    return statusError || requestError ? (
      <IncentiveErrorScreen
        incentiveType={incentiveType}
        errorStep={IncentiveErrorSteps.STATUS}
        errorCode={statusError?.code}
        title={statusError?.title}
        message={statusError?.message}
        isRetrying={isStatusFetching || prerequisite.fetching}
        onRetry={handleRefresh}
      />
    ) : (
      <FullScreenLoading />
    );
  }

  return (
    <>
      <IncentiveDestinationScreen
        incentiveType={incentiveType}
        incentiveProgramId={programId}
        status={status}
        hasMetPrerequisite={prerequisite.isMet === true}
        prerequisiteProduct={prerequisite.product}
        onPrerequisiteRequired={handlePrerequisiteRequired}
        onOptedIn={refreshStatus}
      />
      <IncentivePrerequisiteSheet
        bottomSheetRef={prerequisiteSheetRef}
        incentiveType={incentiveType}
        copy={prerequisite.sheetCopy}
        onStart={handleStartPrerequisitePress}
        onSkip={handleSkipPrerequisitePress}
      />
    </>
  );
}

export default IncentiveResolverScreen;
